import React, { useState } from "react";
import { RAIL } from "../../components/terminal/RailTag.jsx";
import { fmtCcy } from "../../components/terminal/format.js";

/* Bulk mode — CSV batch upload, row-level validation (OK / WARN / ERR),
   rail-mix, policy checks panel, and a Schedule popover. Shape of
   each prop matches BACKEND_TERMINAL_SPEC.md §B.3 + §B.4 verbatim. */

const MOCK_BATCHES = [
  { id:"BATCH-MAY10-PAYROLL", name:"Payroll · May 10",          row_count:147, total:"842300",  currency:"USD",  status:"ready",     created_iso:"May 10 09:14", uploader:"maya@blox.io" },
  { id:"BATCH-MAY09-CTRACT",  name:"Contractor payouts · wk19", row_count:42,  total:"184220",  currency:"USDC", status:"reviewing", created_iso:"May 09 16:42", uploader:"felix@blox.io" },
  { id:"BATCH-MAY08-VENDOR",  name:"Vendor settlement · EU",    row_count:18,  total:"412840",  currency:"EUR",  status:"partial",   created_iso:"May 08 11:08", uploader:"olivia@blox.io" },
  { id:"BATCH-MAY07-GRANT",   name:"Grants · Q2 disbursement",  row_count:8,   total:"1200000", currency:"USD",  status:"sent",      created_iso:"May 07 14:22", uploader:"maya@blox.io" },
];

const MOCK_ROWS = [
  { name:"Adaeze Okafor",      handle:"@adaeze",    rail:"USDC",   amount:"4200",   currency:"USDC", status:"ok",   note:"" },
  { name:"Felix Becker",       handle:"@felix.b",   rail:"SEPA-I", amount:"6800",   currency:"EUR",  status:"ok",   note:"" },
  { name:"Sandeep Patel",      handle:"@sandeep.p", rail:"USDC",   amount:"8500",   currency:"USDC", status:"ok",   note:"" },
  { name:"Ramon Velasquez",    handle:"@ramonv",    rail:"USDC",   amount:"5400",   currency:"USDC", status:"ok",   note:"" },
  { name:"Lucia Fernandez",    handle:"@lucia",     rail:"USDC",   amount:"3800",   currency:"USDC", status:"ok",   note:"" },
  { name:"Hiroshi Tanaka",     handle:"@hiroshi.t", rail:"SWIFT",  amount:"240000", currency:"JPY",  status:"warn", note:"sanctions screen pending" },
  { name:"Maya Chen",          handle:"@maya",      rail:"ACH",    amount:"9200",   currency:"USD",  status:"ok",   note:"" },
  { name:"Jordan Reyes",       handle:"@jordan.r",  rail:"ACH",    amount:"842",    currency:"USD",  status:"ok",   note:"reimbursement" },
  { name:"Olivia Hartmann",    handle:"@olivia.h",  rail:"FPS",    amount:"5400",   currency:"GBP",  status:"ok",   note:"" },
  { name:"Unknown beneficiary",handle:"—",          rail:"—",      amount:"1200",   currency:"USD",  status:"err",  note:"no payout method" },
];

const MOCK_POLICY_CHECKS = [
  { kind:"sanctions",       status:"pending",  count:1, label:"1 pending" },
  { kind:"duplicate",       status:"ok",       label:"clean" },
  { kind:"per_payee_limit", status:"ok",       label:"within bounds" },
  { kind:"approval_route",  status:"required", label:"4-eyes · CFO" },
];

const POLICY_LABEL = {
  sanctions:       "Sanctions screen",
  duplicate:       "Duplicate detect",
  per_payee_limit: "Per-payee limit",
  approval_route:  "Approval routing",
};

const STATUS_COLOR = {
  ready:"#2CCB7E", reviewing:"#F5A524", partial:"#06B6D4", sent:"var(--term-ink-3)",
};

function Stat({ label, value, color, mono }) {
  return (
    <div>
      <div style={{ fontSize:9.5, letterSpacing:"0.16em", color:"var(--term-ink-3)", textTransform:"uppercase", fontWeight:600, marginBottom:4 }}>{label}</div>
      <div className={mono ? "mono" : ""} style={{ padding:"10px 12px", border:"1px solid var(--term-line)", background:"var(--term-bg-pane)", fontSize: mono ? 14 : 13, color: color || "var(--term-ink-1)", fontWeight: mono ? 500 : 400 }}>{value}</div>
    </div>
  );
}

export default function BulkMode({
  batches = MOCK_BATCHES,
  rows = MOCK_ROWS,
  policyChecks = MOCK_POLICY_CHECKS,
}) {
  const [batchIdx, setBatchIdx] = useState(0);
  const [schedOpen, setSchedOpen] = useState(false);
  const [sched, setSched] = useState({ date:"2026-05-12", time:"09:00", tz:"UTC", recur:"once", funding:"auto" });
  const [scheduled, setScheduled] = useState([
    { schedule_id:"SCH-001", batch_id:"BATCH-MAY12-PAYROLL", label:"Payroll · May 12 09:00 UTC", recurrence:"monthly", total_amount:"842300", currency:"USD" },
  ]);
  const b = batches[batchIdx];

  const okCount   = rows.filter((r) => r.status === "ok").length;
  const warnCount = rows.filter((r) => r.status === "warn").length;
  const errCount  = rows.filter((r) => r.status === "err").length;

  const railMix = Array.from(new Set(rows.map((r) => r.rail).filter((r) => r !== "—"))).map((rl) => {
    const railRows = rows.filter((r) => r.rail === rl);
    const total = railRows.reduce((a, r) => a + (parseFloat(r.amount) || 0), 0);
    return { rail: rl, count: railRows.length, total };
  });

  const policyStatusColor = (s) => s === "failed" ? "var(--term-red)" : s === "required" || s === "pending" ? "var(--term-amber)" : s === "warning" ? "var(--term-amber)" : "var(--term-green)";

  return (
    <div style={{ display:"grid", gridTemplateColumns:"360px 1fr 380px", flex:1, minHeight:0 }}>
      {/* LEFT — batches list */}
      <div className="term-pane">
        <div className="term-pane-head">Batches <span className="meta"><b>{batches.length}</b> in flight</span></div>
        <div style={{ padding:"10px 14px", borderBottom:"1px solid var(--term-line-dim)", display:"flex", gap:6 }}>
          <button style={{ flex:1, padding:8, background:"var(--term-green)", color:"#000", border:"1px solid var(--term-green)", fontFamily:"inherit", fontSize:11, fontWeight:600, cursor:"pointer", letterSpacing:"0.04em" }}>+ Upload CSV</button>
          <button style={{ padding:"8px 10px", background:"var(--term-bg-base)", color:"var(--term-ink-2)", border:"1px solid var(--term-line)", fontFamily:"inherit", fontSize:11, cursor:"pointer" }}>API</button>
        </div>
        <div className="term-pane-body">
          <div style={{ display:"grid", gridTemplateColumns:"1fr 60px 80px", gap:8, alignItems:"center", padding:"0 14px", height:22, fontSize:9.5, letterSpacing:"0.12em", color:"var(--term-ink-2)", textTransform:"uppercase", background:"var(--term-bg)", position:"sticky", top:0, zIndex:1, fontWeight:600, borderBottom:"1px solid var(--term-line-dim)" }}>
            <span>BATCH</span><span style={{ textAlign:"right" }}>CT</span><span style={{ textAlign:"right" }}>TOTAL</span>
          </div>
          {batches.map((p, i) => {
            const isActive = batchIdx === i;
            return (
              <div key={p.id} onClick={() => setBatchIdx(i)} style={{
                display:"grid", gridTemplateColumns:"1fr 60px 80px", gap:8, alignItems:"center",
                padding:"0 14px", height:36, borderBottom:"1px solid var(--term-line-dim)",
                cursor:"pointer", position:"relative",
                background: isActive ? "var(--term-bg-pane-2)" : "transparent",
              }}>
                {isActive && <span style={{ position:"absolute", left:0, top:0, bottom:0, width:2, background:"#2CCB7E" }} />}
                <span style={{ display:"flex", flexDirection:"column", gap:2, minWidth:0 }}>
                  <span style={{ fontSize:11.5, color:"var(--term-ink-1)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{p.name}</span>
                  <span className="mono" style={{ fontSize:9.5, color:"var(--term-ink-3)", display:"flex", alignItems:"center", gap:6 }}>
                    <span style={{ width:5, height:5, background: STATUS_COLOR[p.status] }} />
                    {p.status} · {p.created_iso}
                  </span>
                </span>
                <span className="mono" style={{ textAlign:"right", fontSize:10, color:"var(--term-ink-3)" }}>{p.row_count}</span>
                <span className="mono" style={{ textAlign:"right", fontSize:11 }}>{fmtCcy(p.total, p.currency)}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* CENTER — batch preview */}
      <div className="term-pane">
        <div className="term-pane-head">Batch preview <span className="meta">{b.id}</span></div>
        <div style={{ padding:"14px 18px", display:"flex", flexDirection:"column", gap:14, overflow:"auto" }}>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:10 }}>
            <Stat label="ROWS"  value={b.row_count} mono />
            <Stat label="OK"    value={okCount}   mono color="var(--term-green)" />
            <Stat label="WARN"  value={warnCount} mono color="var(--term-amber)" />
            <Stat label="ERROR" value={errCount}  mono color="var(--term-red)" />
          </div>

          <div style={{ border:"1px solid var(--term-line)" }}>
            <div style={{ display:"grid", gridTemplateColumns:"24px 1fr 1fr 60px 90px", gap:8, padding:"8px 12px", background:"var(--term-bg-pane-2)", borderBottom:"1px solid var(--term-line)", fontSize:9, letterSpacing:"0.16em", color:"var(--term-ink-3)", fontWeight:600 }}>
              <span></span><span>BENEFICIARY</span><span>HANDLE</span><span>RAIL</span><span style={{ textAlign:"right" }}>AMOUNT</span>
            </div>
            <div style={{ maxHeight:340, overflow:"auto" }}>
              {rows.map((r, i) => {
                const c = r.status === "err" ? "var(--term-red)" : r.status === "warn" ? "var(--term-amber)" : "var(--term-green)";
                return (
                  <div key={i} style={{ display:"grid", gridTemplateColumns:"24px 1fr 1fr 60px 90px", gap:8, padding:"7px 12px", borderBottom:"1px solid var(--term-line-dim)", fontSize:11, alignItems:"center" }}>
                    <span style={{ width:6, height:6, background:c, borderRadius:"50%", justifySelf:"center" }} title={r.note} />
                    <span style={{ color: r.status === "err" ? "var(--term-red)" : "var(--term-ink-1)" }}>{r.name}</span>
                    <span className="mono" style={{ color:"var(--term-ink-3)", fontSize:10.5 }}>{r.handle}</span>
                    <span className="term-rail-tag" style={{ color:(RAIL[r.rail] || { color:"var(--term-ink-3)" }).color, fontSize:9.5 }}>{r.rail}</span>
                    <span className="mono" style={{ textAlign:"right" }}>{Number(r.amount).toLocaleString()} {r.currency}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div style={{ display:"flex", gap:10, justifyContent:"flex-end", borderTop:"1px solid var(--term-line)", paddingTop:14, position:"relative" }}>
            <button className="term-util-btn">Download report</button>
            <button className="term-util-btn">Fix errors</button>
            <button className="term-util-btn" onClick={() => setSchedOpen((s) => !s)} style={{ borderColor: schedOpen ? "#A78BFA" : "var(--term-line)", color: schedOpen ? "#A78BFA" : "var(--term-ink-2)" }}>⌚ Schedule</button>
            <button disabled={errCount > 0} style={{ background:"var(--term-green)", border:"1px solid var(--term-green)", color:"#000", height:32, padding:"0 18px", fontWeight:600, letterSpacing:"0.04em", fontSize:12, cursor: errCount > 0 ? "not-allowed" : "pointer", opacity: errCount > 0 ? 0.45 : 1, fontFamily:"inherit" }}>Approve batch · {okCount}</button>

            {schedOpen && (
              <div style={{ position:"absolute", right:0, bottom:"calc(100% + 8px)", width:340, background:"var(--term-bg-pane-2)", border:"1px solid var(--term-line)", boxShadow:"0 16px 40px rgba(0,0,0,0.55)", zIndex:20 }}>
                <div style={{ padding:"10px 14px", borderBottom:"1px solid var(--term-line)", display:"flex", alignItems:"baseline", justifyContent:"space-between" }}>
                  <span style={{ fontFamily:"Inter", fontSize:12, fontWeight:500 }}>Schedule batch</span>
                  <span style={{ fontSize:9.5, color:"var(--term-ink-3)", letterSpacing:"0.10em", textTransform:"uppercase" }}>{b.id}</span>
                </div>
                <div style={{ padding:14, display:"flex", flexDirection:"column", gap:10 }}>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                    <label style={{ display:"flex", flexDirection:"column", gap:4, fontSize:9.5, color:"var(--term-ink-3)", letterSpacing:"0.10em", textTransform:"uppercase", fontWeight:600 }}>
                      Date
                      <input type="date" value={sched.date} onChange={(e) => setSched({ ...sched, date: e.target.value })}
                        style={{ background:"var(--term-bg-base)", border:"1px solid var(--term-line)", color:"var(--term-ink-1)", fontFamily:"JetBrains Mono", fontSize:11, padding:"6px 8px" }} />
                    </label>
                    <label style={{ display:"flex", flexDirection:"column", gap:4, fontSize:9.5, color:"var(--term-ink-3)", letterSpacing:"0.10em", textTransform:"uppercase", fontWeight:600 }}>
                      Time
                      <input type="time" value={sched.time} onChange={(e) => setSched({ ...sched, time: e.target.value })}
                        style={{ background:"var(--term-bg-base)", border:"1px solid var(--term-line)", color:"var(--term-ink-1)", fontFamily:"JetBrains Mono", fontSize:11, padding:"6px 8px" }} />
                    </label>
                  </div>
                  <label style={{ display:"flex", flexDirection:"column", gap:4, fontSize:9.5, color:"var(--term-ink-3)", letterSpacing:"0.10em", textTransform:"uppercase", fontWeight:600 }}>
                    Recurrence
                    <div style={{ display:"flex", gap:0, border:"1px solid var(--term-line)" }}>
                      {["once","weekly","biweekly","monthly"].map((r) => (
                        <button key={r} onClick={() => setSched({ ...sched, recur:r })}
                          style={{ flex:1, padding:"7px 4px", background: sched.recur === r ? "#A78BFA" : "transparent", color: sched.recur === r ? "#000" : "var(--term-ink-2)", border:"none", borderRight:"1px solid var(--term-line)", fontFamily:"inherit", fontSize:11, cursor:"pointer", textTransform:"capitalize", fontWeight: sched.recur === r ? 600 : 400 }}>{r}</button>
                      ))}
                    </div>
                  </label>
                  <label style={{ display:"flex", flexDirection:"column", gap:4, fontSize:9.5, color:"var(--term-ink-3)", letterSpacing:"0.10em", textTransform:"uppercase", fontWeight:600 }}>
                    Funding
                    <div style={{ display:"flex", gap:0, border:"1px solid var(--term-line)" }}>
                      {[["auto","Auto-pull"],["prefund","Pre-fund"],["just_in_time","Just-in-time"]].map(([id, l]) => (
                        <button key={id} onClick={() => setSched({ ...sched, funding:id })}
                          style={{ flex:1, padding:"7px 4px", background: sched.funding === id ? "var(--term-bg-pane)" : "transparent", color: sched.funding === id ? "var(--term-ink-1)" : "var(--term-ink-3)", border:"none", borderRight:"1px solid var(--term-line)", fontFamily:"inherit", fontSize:10.5, cursor:"pointer", fontWeight: sched.funding === id ? 600 : 400 }}>{l}</button>
                      ))}
                    </div>
                  </label>
                  <div className="mono" style={{ borderTop:"1px solid var(--term-line-dim)", paddingTop:10, fontSize:10.5, color:"var(--term-ink-3)", display:"flex", justifyContent:"space-between" }}>
                    <span>Will run</span>
                    <span style={{ color:"#A78BFA" }}>{sched.date} {sched.time} {sched.tz} · {sched.recur}</span>
                  </div>
                  <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
                    <button className="term-util-btn" onClick={() => setSchedOpen(false)}>Cancel</button>
                    <button
                      onClick={() => {
                        setScheduled((s) => [{
                          schedule_id:`SCH-${Date.now()}`, batch_id:b.id,
                          label:`${b.name} · ${sched.date} ${sched.time} ${sched.tz}`,
                          recurrence:sched.recur, total_amount:b.total, currency:b.currency,
                        }, ...s]);
                        setSchedOpen(false);
                      }}
                      style={{ background:"#A78BFA", border:"1px solid #A78BFA", color:"#000", height:30, padding:"0 14px", fontWeight:600, letterSpacing:"0.04em", fontSize:11.5, cursor:"pointer", fontFamily:"inherit" }}
                    >Schedule</button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {scheduled.length > 0 && (
            <div style={{ borderTop:"1px solid var(--term-line-dim)", paddingTop:12 }}>
              <div style={{ fontSize:9.5, letterSpacing:"0.16em", color:"var(--term-ink-3)", textTransform:"uppercase", fontWeight:600, marginBottom:8, display:"flex", justifyContent:"space-between" }}>
                <span>Scheduled batches</span>
                <span style={{ color:"#A78BFA" }}>{scheduled.length}</span>
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
                {scheduled.map((s) => (
                  <div key={s.schedule_id} style={{ display:"grid", gridTemplateColumns:"10px 1fr auto auto", gap:10, alignItems:"center", padding:"8px 10px", border:"1px solid var(--term-line-dim)", background:"var(--term-bg-base)" }}>
                    <span style={{ width:6, height:6, background:"#A78BFA", borderRadius:"50%" }} />
                    <span style={{ fontSize:11, color:"var(--term-ink-1)" }}>{s.label}</span>
                    <span style={{ fontSize:9.5, color:"#A78BFA", letterSpacing:"0.10em", textTransform:"uppercase", padding:"2px 6px", border:"1px solid #A78BFA" }}>{s.recurrence}</span>
                    <span className="mono" style={{ fontSize:11 }}>{fmtCcy(s.total_amount, s.currency)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* RIGHT — batch summary + rail mix + policy checks */}
      <div className="term-pane">
        <div className="term-pane-head">Batch summary <span className="meta">{b.uploader}</span></div>
        <div style={{ padding:"14px 18px", display:"flex", flexDirection:"column", gap:14 }}>
          <Stat label="BATCH TOTAL" value={fmtCcy(b.total, b.currency)} mono />
          <div>
            <div style={{ fontSize:9.5, letterSpacing:"0.16em", color:"var(--term-ink-3)", textTransform:"uppercase", fontWeight:600, marginBottom:8 }}>RAIL MIX</div>
            <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
              {railMix.map((rl) => (
                <div key={rl.rail} className="mono" style={{ display:"grid", gridTemplateColumns:"56px 1fr auto", gap:8, fontSize:11 }}>
                  <span className="term-rail-tag" style={{ color:(RAIL[rl.rail] || { color:"var(--term-ink-3)" }).color }}>{rl.rail}</span>
                  <span style={{ color:"var(--term-ink-3)" }}>{rl.count} payments</span>
                  <span>{Number(rl.total).toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div style={{ fontSize:9.5, letterSpacing:"0.16em", color:"var(--term-ink-3)", textTransform:"uppercase", fontWeight:600, marginBottom:8 }}>POLICY CHECKS</div>
            <div className="mono" style={{ display:"flex", flexDirection:"column", gap:4, fontSize:11 }}>
              {policyChecks.map((p) => (
                <div key={p.kind} style={{ display:"flex", justifyContent:"space-between" }}>
                  <span style={{ color:"var(--term-ink-3)" }}>{POLICY_LABEL[p.kind] || p.kind}</span>
                  <span style={{ color: policyStatusColor(p.status) }}>{p.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
